import { useAgTechQueryResultProjection } from "@agtech/core/data/queries/AgTechDataQueries";
import { AgTechFormContext } from "@agtech/core/forms/AgTechFormContext";
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridNumberControl } from "@agtech/web/components/Grids/Controls/AgTechGridNumberControl";
import { gridItems } from "@agtech/web/components/Grids/Data/AgTechGridProps";
import AgTechFormDialog from "@agtech/web/components/Portals/Dialogs/AgTechFormDialog";
import { SaleLot } from "@sales/data/lots/LotData";
import { useLotDataWriteSaleReorderAction } from "@sales/data/lots/LotDataActions";
import { useLotDataGetSaleLotDataQuery } from "@sales/data/lots/LotDataQueries";

type LotSaleOrderUpdateGridItem = SaleLot & {
    originalSaleOrder: number,
    wasChanged: boolean
}

const LotSaleOrderUpdateDialog = () => {
    const dialogQuery = useDialogQuery();
    const dialogAction = useLotDataWriteSaleReorderAction();

    return (
        <AgTechFormDialog
            dialogData={{
                query: dialogQuery,
                getDialogData: d => d
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d
            }}
            structure={() => ({
                header: 'Change Lot Sale Order',
                submitButtonText: 'Submit'
            })}
            width={800}
        >
            {(formContext) => (
                <AgTechFormBody classes='py-3'>
                    <h1 className="text-base text-detail mb-5">Use the grid below to update the sale order of multiple lots at once. When done, click the submit button to submit your sale order updates.</h1>
                    <SaleLotOrderModificationGrid formContext={formContext} />
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    );
};

const useDialogQuery = () => {
    const lotDataQuery = useLotDataGetSaleLotDataQuery();

    return useAgTechQueryResultProjection({
        queryRun: lotDataQuery,
        projection: data => Object.values(data.lots).map<LotSaleOrderUpdateGridItem>(lot => ({
            ...lot,
            originalSaleOrder: lot.saleOrder,
            wasChanged: false
        }))
    })
}

const SaleLotOrderModificationGrid = (props: {
    formContext: AgTechFormContext<LotSaleOrderUpdateGridItem[]>
}) => {
    const lots = props.formContext.entity;

    return (
        <AgTechGrid
            header={{
                title: 'Lots In Sale'
            }}
            data={{
                itemQuery: gridItems(lots)
            }}
            columns={[
                {
                    header: 'New Sale Order',
                    value: (l, update) => (
                        <AgTechGridNumberControl
                            value={l.saleOrder}
                            onUpdated={val => {
                                const lotIndex = lots.findIndex(e => e.saleOrder === val);
                                
                                const nextOrders = lots.map((c, i) => {
                                    if (i === lotIndex) {
                                        c.saleOrder = 0;
                                        c.wasChanged = true;
                                    }
    
                                    if (c.originalSaleOrder === l.originalSaleOrder) {
                                        c.saleOrder = val ?? 0;
                                        c.wasChanged = val !== c.originalSaleOrder;
                                    }
    
                                    return c;
                                });

                                return nextOrders;

                                //props.formContext.updateFormEntity(() => [...nextOrders]);
                            }}
                        />
                    ),
                    width: '130px',
                    itemBasedClasses: item => item.saleOrder === 0 ? 'invalid' : ''
                },
                {
                    header: 'Old Sale Order',
                    value: l => l.originalSaleOrder,
                    width: '130px',
                    classes: 'centered emphasized bg-slate-100'
                },
                {
                    header: 'Lot Number',
                    value: l => l.lotNumber,
                    width: '120px',
                    classes: 'centered emphasized bg-slate-100'
                },
                {
                    header: 'Lot Description',
                    value: l => l.lotDescription,
                    classes: 'centered emphasized bg-slate-100'
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return prev.id === curr.id && prev.lotNumber === curr.lotNumber && prev.saleOrder === curr.saleOrder;
            }}
            sortingConfig={{
                getSortingKey: lot => lot.saleOrder
            }}
        />
    );
}

LotSaleOrderUpdateDialog.Open = (e: AgTechClickEventHandler) => e.dialogs.openDialog({
    dialog: () => <LotSaleOrderUpdateDialog />
})

LotSaleOrderUpdateDialog.Button = (props: AgTechButtonProps) => {
    const buttonProps: AgTechButtonProps = {
        ...props,
        text: 'Change Sale Order',
        icon: 'fas fa-arrow-down-1-9',
        action: LotSaleOrderUpdateDialog.Open
    };

    return <AgTechButton {...buttonProps} />;
};

export default LotSaleOrderUpdateDialog;